import { Group, type Material, Mesh } from 'three';
import type { Palette } from '../palette';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';

const TAU = Math.PI * 2;

/* Airframe stations, metres. Nose is north (-z), tail south (+z); the ground is y = 0 when landed. */
const BELLY = 0.55;
const ROOF = 2.85;
const WING_Z = -1.0;
const WING_TOP = 3.55;
const NACELLE_X = 7.4;
const ROTOR_Y = 6.2;
const ROTOR_RADIUS = 4.5;
/** True proprotor speed strobes against a 60 Hz frame; this reads as "turning fast" instead. */
const ROTOR_REV_PER_S = 4;
/** The ramp hinges at the cabin floor; at this pitch its lip just rests on the ground when landed. */
const RAMP_PITCH = 0.154;
/** Lip of the lowered ramp: where the rope ladder is made fast. */
const LADDER_Y = 0.1;
const LADDER_Z = 6.95;
const RUNG_SPACING = 0.4;

export interface OspreyModel {
  /** Root group; origin is on the ground directly under the fuselage centre when landed; the model hovers by moving root.position.y. Nose points north (-z). */
  root: Group;
  /** Spin the two proprotors and sway the rope ladder. */
  update(time: number, dt: number): void;
  /** Show/hide the rope ladder hanging from the rear ramp down to the ground (length = current hover height). */
  setLadder(visible: boolean, hoverHeight: number): void;
}

/**
 * Everything that does not move. From the fixed camera only tops and south faces are ever
 * seen, so that is where the markings and the glazing go.
 */
function airframe(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  const lo = p.color('metal.lo');
  const mid = p.color('metal.mid');
  const hi = p.color('metal.hi');
  const glass = p.color('ink.1');
  const accent = p.color('playerAccent.0');
  const skin: BoxColors = { all: mid, top: hi, bottom: lo };
  const surface: BoxColors = { all: mid, top: hi, front: lo };

  // Undercarriage: nose leg and a pair of mains under the sponsons.
  mb.box(0.35, 0.6, 0.7, glass, place(0, 0, -5.4));
  for (const sx of [-1, 1]) mb.box(0.45, 0.6, 1.0, glass, place(sx * 1.55, 0, 1.4));

  // Cabin. Its south face shows only below the upswept tail: the open cargo door.
  mb.box(2.5, ROOF - BELLY, 9.4, { ...skin, front: glass }, place(0, BELLY, -0.8));
  for (const sx of [-1, 1]) mb.box(0.85, 1.0, 5.4, skin, place(sx * 1.6, 0.45, 0.7)); // sponsons

  // Nose, dark radome and the refuelling probe on the starboard side.
  mb.box(2.2, 1.95, 1.7, { ...skin, top: lo }, place(0, BELLY + 0.05, -6.3));
  mb.box(1.5, 1.3, 1.1, { all: lo, top: mid }, place(0, BELLY + 0.2, -7.65));
  mb.box(0.16, 0.16, 0.8, lo, place(0.55, BELLY + 0.5, -8.5));

  // Raised cockpit: dark glazing under a cross of framing, standing proud of the cabin roof.
  mb.box(2.0, 0.85, 2.3, glass, place(0, 2.3, -6.0));
  mb.box(0.2, 0.06, 2.3, mid, place(0, 3.15, -6.0));
  mb.box(2.0, 0.06, 0.2, mid, place(0, 3.15, -5.7));
  mb.box(2.1, 0.4, 0.4, skin, place(0, ROOF, -4.75));

  // High wing on its fairing, flaperons along the trailing edge.
  mb.box(3.0, 0.5, 3.6, skin, place(0, ROOF, WING_Z));
  mb.box(15.0, 0.4, 2.6, surface, place(0, WING_TOP - 0.4, WING_Z));
  for (const sx of [-1, 1]) {
    mb.box(5.4, 0.05, 0.55, mid, place(sx * 4.0, WING_TOP, WING_Z + 0.95));
    mb.box(0.7, 0.08, 2.64, accent, place(sx * 5.9, WING_TOP, WING_Z)); // friendly bands (05 §4.3)
  }
  mb.box(1.3, 0.05, 0.45, p.color('paper.0'), place(-3.4, WING_TOP, WING_Z - 0.4));

  // Nacelles upright for the hover: exhaust below, gearbox and spinner above.
  for (const sx of [-1, 1]) {
    const x = sx * NACELLE_X;
    mb.box(1.0, 0.45, 1.3, glass, place(x, 1.25, WING_Z));
    mb.box(1.4, 3.6, 1.8, { all: mid, top: lo }, place(x, 1.7, WING_Z));
    mb.box(1.44, 0.3, 1.84, accent, place(x, 4.6, WING_Z));
    mb.box(1.1, 0.7, 1.4, skin, place(x, 5.3, WING_Z));
    mb.cylinder(0.5, 0.22, 0.75, 6, lo, place(x, 6.0, WING_Z));
  }

  // Roof furniture: a hatch, and the accent band that marks her as ours from above.
  mb.box(1.0, 0.05, 1.2, mid, place(0, ROOF, 1.1));
  mb.box(2.54, 0.08, 0.8, accent, place(0, ROOF, 2.7));

  // Upswept rear fuselage, tail boom, and the ramp let down beneath them.
  mb.box(2.4, 1.75, 2.5, skin, place(0, 1.1, 5.15));
  mb.box(1.8, 1.25, 2.4, skin, place(0, 1.6, 7.6));
  mb.with(place(0, BELLY + 0.05, 3.9, 0, 1, RAMP_PITCH), () => {
    mb.box(2.0, 0.14, 3.0, { all: lo, top: mid }, place(0, -0.14, 1.5));
  });

  // H-tail: a broad stabiliser carrying twin fins with accent flashes.
  mb.box(6.0, 0.25, 1.7, surface, place(0, ROOF, 7.9));
  for (const sx of [-1, 1]) {
    mb.box(0.3, 2.7, 1.8, skin, place(sx * 2.9, 2.3, 7.9));
    mb.box(0.34, 0.5, 1.84, accent, place(sx * 2.9, 4.5, 7.9));
  }
  return mb;
}

/** One three-blade proprotor about its own origin. Blades are wide so they survive 18 px/m. */
function proprotor(p: Palette): MeshBuilder {
  const mb = new MeshBuilder();
  const blade: BoxColors = { all: p.color('ink.1'), top: p.color('metal.lo') };
  const tip = 0.5;
  const root = 0.3;
  const span = ROTOR_RADIUS - tip - root;
  for (let i = 0; i < 3; i++) {
    mb.with(place(0, 0, 0, (i / 3) * TAU), () => {
      mb.box(0.8, 0.12, span, blade, place(0, 0, root + span / 2));
      mb.box(0.8, 0.12, tip, p.color('playerAccent.0'), place(0, 0, ROTOR_RADIUS - tip / 2));
    });
  }
  mb.cylinder(0.6, 0.6, 0.2, 6, p.color('metal.lo'), place(0, -0.04, 0));
  return mb;
}

/** Rope ladder hanging from y = 0 down to -rungs * RUNG_SPACING. */
function ropeLadder(p: Palette, rungs: number): MeshBuilder {
  const mb = new MeshBuilder();
  const length = rungs * RUNG_SPACING;
  for (const sx of [-1, 1]) mb.box(0.1, length, 0.1, p.color('canvas.lo'), place(sx * 0.3, -length, 0));
  for (let i = 0; i < rungs; i++) {
    mb.box(0.6, 0.1, 0.1, p.color('timber.mid'), place(0, -(i + 0.5) * RUNG_SPACING - 0.05, 0));
  }
  return mb;
}

/** V-22 in hover configuration (02 §3): the ranger's ride in and, with luck, out. */
export function buildOsprey(palette: Palette, material: Material): OspreyModel {
  const root = new Group();
  root.name = 'osprey';

  const body = new Mesh(airframe(palette).build(), material);
  body.castShadow = true;
  body.receiveShadow = true;
  root.add(body);

  // Both rotors share one geometry; each sits in its own group so it can turn.
  const rotorGeometry = proprotor(palette).build();
  const rotors: Group[] = [];
  for (const sx of [-1, 1]) {
    const hub = new Group();
    hub.position.set(sx * NACELLE_X, ROTOR_Y, WING_Z);
    const blades = new Mesh(rotorGeometry, material);
    blades.castShadow = true;
    hub.add(blades);
    root.add(hub);
    rotors.push(hub);
  }

  // The ladder pivots where it is made fast, so the sway swings its foot, not its head.
  const ladder = new Group();
  ladder.position.set(0, LADDER_Y, LADDER_Z);
  ladder.visible = false;
  root.add(ladder);
  let ladderMesh: Mesh | null = null;
  let ladderRungs = 0;
  let spin = 0;

  return {
    root,
    update(time, dt) {
      spin = (spin + ROTOR_REV_PER_S * TAU * dt) % TAU;
      rotors.forEach((hub, i) => {
        // Handed, as on the real aircraft, and out of phase so the pair never looks mirrored.
        hub.rotation.y = i === 0 ? spin : Math.PI / 3 - spin;
      });
      ladder.rotation.z = Math.sin(time * 1.7) * 0.05;
      ladder.rotation.x = Math.sin(time * 1.1 + 0.8) * 0.04;
    },
    setLadder(visible, hoverHeight) {
      ladder.visible = visible;
      if (!visible) return;
      const length = Math.max(0, hoverHeight) + LADDER_Y;
      const rungs = Math.max(1, Math.round(length / RUNG_SPACING));
      // Rebuild only when a rung is gained or lost; in between, stretch the last fraction.
      if (rungs !== ladderRungs || !ladderMesh) {
        if (ladderMesh) {
          ladder.remove(ladderMesh);
          ladderMesh.geometry.dispose();
        }
        ladderMesh = new Mesh(ropeLadder(palette, rungs).build(), material);
        ladderMesh.castShadow = true;
        ladder.add(ladderMesh);
        ladderRungs = rungs;
      }
      ladderMesh.scale.y = length / (rungs * RUNG_SPACING);
    },
  };
}
